import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { TriggerButton } from "widgets";

import { type InputTableQuerySpec } from "@/graphql.ts";
import { loadVisibleSampleIdsFromParentIds } from "@/redux/modules/sample.ts";
import { createTempGroup } from "@/redux/modules/saved-group.ts";
import {
  createTableSpecForGroupParent,
  getTableQuerySpecWithSelection,
  type ITableData,
} from "@/redux/modules/table-data.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE } from "@/settings/settings.ts";

interface ISampleChartButtonProps {
  activeTable: ITableData;
}

/**
 * Button that allows to push a selection of samples (or full group) to the analytical viewer.
 *
 */
const SampleChartButton: FC<ISampleChartButtonProps> = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<ReduxDispatch>();
  const visualizerBasePath = "/visualizer/data/group/";
  const chartSampleSelection = useCallback(() => {
    const table: ITableData = props.activeTable;

    if (
      // if it's a sample group, use it directly
      table.tableQuerySpec?.idType === DATATABLE_ID_TYPE.GROUP &&
      table.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE
    ) {
      navigate(`${visualizerBasePath}${table.tableQuerySpec.id}`);
      return;
    } else {
      // create a group in the saved-group slice and pass the ID to chartSampleGroup
      const selectedIds: number[] =
        table.selectionModel.selectedIds.length > 0 ? (table.selectionModel.selectedIds as number[]) : table.rowIds;
      const tableQuerySpec: InputTableQuerySpec = getTableQuerySpecWithSelection(table, selectedIds);
      if (table.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE) {
        // just create the group from sample table spec
        return dispatch(createTempGroup(tableQuerySpec)).then((groupId: number) =>
          navigate(`${visualizerBasePath}${groupId}`),
        );
      } else if (table.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE_PARENT) {
        // either use parent selection, or fall back to all parents
        const parentIds = table.selectionModel.selectedIds as number[];
        // get the visible sample IDs related to these parents
        return dispatch(loadVisibleSampleIdsFromParentIds(parentIds)).then((sampleIds: number[]) => {
          // assemble sample table spec using the sample IDs
          let sampleTableQuerySpec: InputTableQuerySpec = createTableSpecForGroupParent(ENTITY_TYPE.SAMPLE);
          sampleTableQuerySpec = {
            ...sampleTableQuerySpec,
            restrictions: {
              selection: {
                selectedResultIds: sampleIds,
              },
            },
          };
          // create temporary sample group and launch analytical data viewer
          dispatch(createTempGroup(sampleTableQuerySpec)).then((groupId: number) =>
            navigate(`${visualizerBasePath}${groupId}`),
          );
        });
      }
    }
  }, [props.activeTable]);

  // when showing the button in parent context, it only gets active when there is a selection
  const disabled: boolean =
    props.activeTable.tableQuerySpec?.resultType === ENTITY_TYPE.SAMPLE_PARENT &&
    props.activeTable.selectionModel.selectedIds.length === 0;

  const dataPresent: boolean = props.activeTable.rowIds && props.activeTable.rowIds.length > 0;

  return (
    <TriggerButton
      icon={"bar_chart"}
      label={"Chart"}
      key={"sample-chart-btn-header"}
      tooltip={
        disabled ? "No rows selected" : dataPresent ? "Visualize data for the selected samples" : "Nothing to visualize"
      }
      onPress={chartSampleSelection}
      showLabel
      isDisabled={disabled}
    />
  );
};

export default SampleChartButton;
